/**
 * Client-side ability display names per slot — AUTO-DERIVED from content
 * (#274; supersedes the hand-maintained `LABELS_BY_CHAMPION` table).
 *
 * WHY THIS EXISTS: the client deliberately does NOT load the full content
 * bundle into the browser (see `catalog.ts`), and the wire snapshot carries
 * per-slot cast/cooldown state but NOT ability display names. To render the
 * action bar with a human-readable name/"icon" (first letter) we resolve
 * `championId` + `slot` → ability name via `abilityCatalog.ts`'s build-time
 * derivation from `packages/content/data/champions/*.json` +
 * `packages/content/data/abilities/*.json` — the same glob + narrow-schema
 * pattern as the champion picker's catalog (#239). There is no
 * balance/competitive reason to hide an ability's NAME (it's already fully
 * conveyed to the opposing player the instant it's cast), so this is a safe
 * thing to auto-derive, unlike the ability's numeric/behavioral shape.
 *
 * This file no longer holds any hand-maintained per-champion data — adding a
 * champion or reordering/renaming/adding/removing abilities in its content
 * JSON requires ZERO edits here for names/slot-order to stay correct.
 *
 * SIBLING: `abilityIcons.ts` (#72) is the icon counterpart of this table —
 * same champion+slot shape, resolving to real art with a letter-glyph
 * fallback, still hand-maintained (no icon art exists yet for any champion).
 * Names are text; icons are art; both stay client-side and never touch the
 * sim/content hash.
 */

import { toChampionId, type AuraDefId, type ChampionId } from "@pit/protocol";
import { ABILITY_NAMES_BY_CHAMPION, resolveDisplayAbilityMeta } from "./abilityCatalog.js";

/**
 * Number of ability slots the action bar renders. #322: bumped 8 → 12 — 8
 * was too small for the shipped Priest kit (11 abilities, #334), so slots
 * 8-10 rendered as reserved/empty no matter how correct the label table was.
 * The default keymap (`keymap.ts`'s `DEFAULT_CAST_SLOTS`) now also binds all
 * 12 slots out of the box — keys "1".."8" for slots 0-7, q/e/r/` for slots
 * 8-11 (#407) — so `ActionSlot`'s `castSlotKeyLabel(...) ?? String(slot + 1)`
 * unbound-fallback is only ever exercised after a player clears a binding.
 * The action bar also renders a distinct, always-leftmost auto-attack icon
 * OUTSIDE this count (13 icons total) — see `ActionBar`'s `AutoAttackSlot`.
 */
export const ACTION_SLOT_COUNT = 12;

/** Fallback when a champion has no derived names yet: all slots empty. */
const EMPTY_LABELS: readonly (string | null)[] = Array.from(
  { length: ACTION_SLOT_COUNT },
  () => null,
);

/**
 * Display name for `slot` on `championId`, or `null` if that slot has no
 * ability (or the champion/slot is unknown). Pure lookup over the
 * content-derived `ABILITY_NAMES_BY_CHAMPION` map.
 */
export function abilityLabel(championId: ChampionId, slot: number): string | null {
  const names = ABILITY_NAMES_BY_CHAMPION.get(championId);
  const table = names ?? EMPTY_LABELS;
  return table[slot] ?? null;
}

/**
 * #76: display name for `slot` on `championId`, resolved through the SAME
 * state-conditional rule the sim's `resolveSlotAbility` uses (`holdsAura`) —
 * e.g. shows "Ambush" instead of "Backstab" on Rogue's Backstab slot while
 * `selfAuras` carries an unexpired Stealth. `selfAuras`/`tick` come straight
 * off the wire (`WireEntity.auras`, already sent for #91's `SelfAuraBar`) —
 * no new wire field. Falls back to `abilityLabel`'s ordinary (unconditional)
 * result for a champion/slot with no conditional entry. PURELY COSMETIC — see
 * `resolveDisplayAbilityMeta`'s doc comment for the "cosmetic glitch, not a
 * correctness bug" tradeoff.
 */
export function abilityLabelResolved(
  championId: ChampionId,
  slot: number,
  selfAuras: readonly { readonly def: AuraDefId; readonly expiresAtTick: number | null }[],
  tick: number,
): string | null {
  return resolveDisplayAbilityMeta(championId, slot, selfAuras, tick)?.name ?? abilityLabel(championId, slot);
}

/**
 * The single-character placeholder "icon" for a slot: first letter of the
 * ability name, uppercased, or `""` for an empty slot.
 */
export function abilityIconLetter(championId: ChampionId, slot: number): string {
  const name = abilityLabel(championId, slot);
  return name === null ? "" : name.charAt(0).toUpperCase();
}

/** #76: `abilityIconLetter`, resolved through the same conditional rule as
 *  `abilityLabelResolved` — see its doc comment. */
export function abilityIconLetterResolved(
  championId: ChampionId,
  slot: number,
  selfAuras: readonly { readonly def: AuraDefId; readonly expiresAtTick: number | null }[],
  tick: number,
): string {
  const name = abilityLabelResolved(championId, slot, selfAuras, tick);
  return name === null ? "" : name.charAt(0).toUpperCase();
}

/**
 * Default champion for the HUD until per-match champion is tracked in store.
 * Literal opaque id (#546 lesson) — see #271/#275 for the bug class a
 * hardcoded HUD default guards against (a HUD component reading a stale
 * default instead of the real runtime champion). Previously resolved via
 * `catalog.ts`'s `championBySlugOrThrow("warrior")`, which was slug-keyed
 * and so needed the same hand-edit on rename as a literal id would, just
 * with extra indirection (#546 removed that helper; see git history if a
 * genuine slug lookup is needed again, e.g. URL routing).
 */
// eslint-disable-next-line pit/no-hardcoded-champion-id
export const DEFAULT_HUD_CHAMPION_ID: ChampionId = toChampionId("chm_BJ4RLXqiBG"); // slug: fighter (formerly warrior, #546)
